import NetInfo from '@react-native-community/netinfo';
import { useEffect } from 'react';
import { useLocalObservable } from 'mobx-react';
import { useSession } from './useSession';
import { useStores } from '../useStores';


interface TreeFetchData {
  dataId: string;
  dataType: DataType;
  pathId?: string;
  reckoningId?: string;
  fromPath?: boolean;
  fromReckoning?: boolean;
}
export enum DataType {
  SURVEY = 'SURVEY',
  SESSION = 'SESSION',
}
export const useFetchedTree = ({
  dataId,
  dataType,
  pathId,
  reckoningId,
  fromPath,
  fromReckoning,
}: TreeFetchData) => {
  const { trees, session } = useStores();

  // every tree is a session tree: a survey plays as a block inside one
  const sessionId = dataId;

  useSession({sessionId, pathId, reckoningId, fromReckoning, fromPath});

  // Load tree with proper dependency management. Entering a session always
  // refetches: the root store is persisted, so the tree (and the session
  // steps, see useSession) could otherwise come from a previous app run and
  // miss content changed server-side since (e.g. grafted activity frames)
  useEffect(() => {
    if (dataId && dataType) {
      trees.loadTree(dataId, dataType, true);
    }
  }, [dataId, dataType, trees]);

  // Create a local observable store to track tree state reactively
  const localStore = useLocalObservable(() => ({
    get treeState() {
      if (!dataId || !dataType) {
        return { data: undefined, loading: true, error: false };
      }

      // get state from store in reactive context (inside local observable closure)
      const sessionLoading = session.state === 'pending';
      const sessionError = session.state === 'error';

      const tree = trees.getTree(dataId);
      return {
        data: tree.data,
        loading: tree.loading || sessionLoading,
        error: tree.error || sessionError,
      };
    }
  }));

  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener(state => {
      if (state.isConnected && dataId && dataType) {
        trees.loadTree(dataId, dataType);
      }
    });
    return unsubscribe;
  }, [dataId, dataType, trees]);

  return localStore;
};
